/* Size classes (rule 1) and the navigation they imply. Exact breakpoints: 600, 840, 1200, 1600.
   compact (<600, cover 411 dp): bottom bar, one pane.
   medium (600-839, open fold 752 dp): 96 dp rail, two panes forced (REUSE.md device spec).
   expanded (840-1199, DeX and Windows windows): collapsed 64 px sidebar, two panes.
   large / xl (>= 1200, web): full 264 px sidebar, collapsible with Ctrl+B. */
import { useLayoutEffect, useState, type RefObject } from 'react';

export type SizeClass = 'compact' | 'medium' | 'expanded' | 'large' | 'xl';
export type NavKind = 'bottom' | 'sidebar';
/** Below this width the sidebar is a drawer behind one Menu button, with a bottom bar. */
export const DRAWER_BELOW = 768;

export function sizeClass(width: number): SizeClass {
  if (width < 600) return 'compact';
  if (width < 840) return 'medium';
  if (width < 1200) return 'expanded';
  if (width < 1600) return 'large';
  return 'xl';
}

/** Navigation by shell width: a permanent sidebar from 768 px, a drawer plus bottom bar below it. */
export function navForWidth(width: number): NavKind {
  return width < DRAWER_BELOW ? 'bottom' : 'sidebar';
}

/** Measures an element's width with ResizeObserver (falls back to the window width). */
export function useElementWidth(ref: RefObject<HTMLElement | null>, fallback = typeof window !== 'undefined' ? window.innerWidth : 1280): number {
  const [w, setW] = useState(fallback);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setW(el.getBoundingClientRect().width || el.clientWidth || window.innerWidth);
    read();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', read);
      return () => window.removeEventListener('resize', read);
    }
    const ro = new ResizeObserver((entries) => {
      const e = entries[0];
      const width = e?.contentRect?.width;
      setW(width && width > 0 ? width : el.getBoundingClientRect().width || window.innerWidth);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, [ref]);
  return w;
}
